import {
  Goal,
  GroupByMode,
  ID,
  LinkedItemSection,
  ReorderSectionUpdate,
  SortingMode,
  UpdateGoal,
} from '@griply/types';

export type BuildGoalReorderUpdateOptions = {
  goal: Goal;
  subGoals: Goal[];
  updatedSections: ReorderSectionUpdate[];
  groupBy: GroupByMode | undefined | null;
  /** The list's root pseudo-section id — carries the section order (Section
   *  mode) or the sub-goal order (Goal mode). */
  rootSectionId: ID;
  /** The list's "no section" bucket id — carries the flat task order in
   *  Section mode for tasks not assigned to any section. */
  noGroupingSectionId: ID;
};

type CustomTaskOrderKey = Exclude<
  GroupByMode,
  GroupByMode.None | GroupByMode.LifeArea
>;

const withCustomSort = (goal: Goal) => ({
  ...(goal.filterDocument ?? {}),
  sortingMode: SortingMode.Custom,
});

export const buildGoalReorderUpdate = ({
  goal,
  subGoals,
  updatedSections,
  groupBy,
  rootSectionId,
  noGroupingSectionId,
}: BuildGoalReorderUpdateOptions): UpdateGoal[] => {
  const groupByMode = groupBy ?? GroupByMode.Section;

  if (groupByMode === GroupByMode.Section) {
    const sectionMap = new Map(
      (goal.sections ?? []).map((section) => [section.id, section]),
    );

    const newSections: LinkedItemSection[] = [];
    let rootOrder = goal.sections?.map(({ id }) => id) ?? [];

    updatedSections.forEach((updatedSection) => {
      if (updatedSection.id === rootSectionId) {
        rootOrder = updatedSection.taskSorting;
        return;
      }

      const originalSection = sectionMap.get(updatedSection.id);
      if (originalSection) {
        newSections.push({
          ...originalSection,
          tasks: updatedSection.taskSorting,
        });
        sectionMap.delete(updatedSection.id);
      }
    });

    sectionMap.forEach((remainingSection) =>
      newSections.push(remainingSection),
    );

    newSections.sort(
      (a, b) => rootOrder.indexOf(a.id) - rootOrder.indexOf(b.id),
    );

    return [
      {
        id: goal.id,
        sections: newSections,
        taskSorting:
          updatedSections.find((s) => s.id === noGroupingSectionId)
            ?.taskSorting ?? goal.taskSorting,
        taskSortingMode: SortingMode.Custom,
        filterDocument: withCustomSort(goal),
      },
    ];
  }

  if (groupByMode === GroupByMode.Goal) {
    const updates: UpdateGoal[] = [];

    updatedSections.forEach((section) => {
      if (section.id === rootSectionId) {
        updates.push({ id: goal.id, goalSorting: section.taskSorting });
        return;
      }

      if (section.id === goal.id) {
        updates.push({
          id: goal.id,
          taskSorting: section.taskSorting,
          taskSortingMode: SortingMode.Custom,
          filterDocument: withCustomSort(goal),
        });
        return;
      }

      const subGoal = subGoals.find((g) => g.id === section.id);
      if (subGoal) {
        updates.push({
          id: subGoal.id,
          taskSorting: section.taskSorting,
          taskSortingMode: SortingMode.Custom,
          filterDocument: {
            ...(subGoal.filterDocument ?? {}),
            sortingMode: SortingMode.Custom,
          },
        });
      }
    });

    return updates;
  }

  const groupBySorting = updatedSections.reduce<Record<ID, ID[]>>(
    (acc, section) => {
      if (section.id === rootSectionId) {
        return acc;
      }
      acc[section.id] = section.taskSorting;
      return acc;
    },
    {},
  );

  if (!Object.keys(groupBySorting).length) {
    return [];
  }

  const customOrderKey = groupByMode as CustomTaskOrderKey;

  return [
    {
      id: goal.id,
      customTaskOrder: {
        ...(goal.customTaskOrder ?? {}),
        [customOrderKey]: {
          ...(goal.customTaskOrder?.[customOrderKey] ?? {}),
          ...groupBySorting,
        },
      },
      taskSortingMode: SortingMode.Custom,
      filterDocument: withCustomSort(goal),
    },
  ];
};
